import React, { Component, Fragment, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Col, Label, FormGroup, Row, Input, Button, SimpleAlert, FormSection, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from 'twyne-new.ui';

import Ifficient from './Partners/Ifficient';
import Twyne from './Partners/Twyne';
import OptIntelligence from './Partners/OptIntelligence';
import Tiburon from './Partners/Tiburon';
import PermissionData from './Partners/PermissionData';
import CovalentPartner from './Partners/Covalent';
import Nitro from './Partners/Nitro';
import RexadzPartner from './Partners/Rexadz';
import Fluent from './Partners/Fluent';

import InfoTip from 'components/InfoTip';
import Select from 'components/formsy/Select';
import Inputy from 'components/formsy/Input';
import URLView from 'components/InputCopyURL';
import { FormFragment } from 'components/formsy/FormComponent';
import { useRevenuePostbackParamsTips } from '../../utils';
import MediaAlpha from './Partners/MediaAlpha';
import { ChevronDown, Trash } from 'lucide-react';

const REVENUE_POSTBACK_ORIGIN = new URL(window.location)
REVENUE_POSTBACK_ORIGIN.pathname = '/revenue/postback'
if (REVENUE_POSTBACK_ORIGIN.port)
  REVENUE_POSTBACK_ORIGIN.port = 3000

const TRANSACTION_TYPE_OPTIONS = [
  { label: 'Aggregated (override)', value: 'aggregated' },
  { label: 'Incremental', value: 'incremental' },
]
const PARTNERS_OPTIONS = [
  { label: 'Twyne', value: 'twyne' },
  { label: 'Ifficient', value: 'ifficient' },
  { label: 'OptIntelligence', value: 'optintelligence' },
  { label: 'Tiburon', value: 'tiburon' },
  { label: 'Permissiondata', value: 'permissiondata' },
  { label: 'Covalent', value: 'covalent' },
  { label: 'Nitro', value: 'nitro' },
  { label: 'Rexadz', value: 'rexadz' },
  { label: 'Fluent', value: 'fluent' },
  { label: 'Media Alpha', value: 'mediaalpha' },
]

const propTypes = {
  type: PropTypes.string,
  vendor: PropTypes.string,
  configuration: PropTypes.object,
  onCustomChange: PropTypes.func,
};

export default class RevenueForm extends Component {
  state = {}

  usePostbackURL = (defaultURL) => {
    const { siteId, integrationId } = this.props

    if (!integrationId)
      return { url: null, params: {} }

    const [url, setUrl] = useState('')

    const transidOptional = !this.state.isIncremental
    useEffect(() => {
      if (!transidOptional)
        params.transid.add()
    }, [transidOptional])

    const params = useMemo(() => {
      const url = new URL(defaultURL || '', REVENUE_POSTBACK_ORIGIN)
      url.host = REVENUE_POSTBACK_ORIGIN.host
      url.protocol = REVENUE_POSTBACK_ORIGIN.protocol
      url.pathname = `${REVENUE_POSTBACK_ORIGIN.pathname}/${siteId}/${integrationId}`

      const appendParam = (key, value) => !url.searchParams.has(key) && url.searchParams.append(key, value)
      appendParam('pekey', '{pekey}')
      appendParam('revenue', '{revenue}')

      const update = () => setUrl(decodeURI(url.href))
      update()

      const buildParam = (param, template, optional) => ({
        optional,
        change: param !== 'subid' ? null : value => {
          url.searchParams.set(param, value)
          update()
        },
        get value() { return url.searchParams.get(param) },
        add(remove) {
          if ((this.isAdded || remove) && optional)
            url.searchParams.delete(param)
          else if (!this.isAdded)
            url.searchParams.append(param, template)
          update()
        },
        get isAdded() { return url.searchParams.has(param) }
      })

      return {
        subid: buildParam('subid', '{external_group_id}', true),
        transid: buildParam('transid', '{unique_transaction_value}', transidOptional),
        format: buildParam('format', 'image', true),
        pekey: { get value() { return url.searchParams.get('pekey') } },
        revenue: { get value() { return url.searchParams.get('revenue') } },
      }

    }, [transidOptional])

    return { url, params }
  }

  PostbackParam = ({ name, data: { [name]: data } }) => {
    if (!data)
      return null

    const { value, change, disabled = !change, isAdded, optional } = data

    if (optional && !isAdded)
      return null

    const { KeyTip, ValueTip } = useRevenuePostbackParamsTips(name)

    return <Fragment>
      <Row>
        <Col size='6'>
          <FormGroup>
            <KeyTip>
              <Input value={name} disabled />
            </KeyTip>
          </FormGroup>
        </Col>
        <Col size={!optional ? '6' :'5'}>
          <FormGroup>
            <ValueTip>
              <Input value={value} disabled={disabled} onChange={({ target }) => change && change(target.value)} />
            </ValueTip>
          </FormGroup>
        </Col>
        {!optional ? null : <Col size='1'>
          <FormGroup>
            <Button onClick={() => data.add(true)} color='danger' variant='ghost'>
              <Trash size={16} />
            </Button>
          </FormGroup>
        </Col>}
      </Row>
    </Fragment>
  }

  PostbackURL = ({ data }) => {
    const { url, params } = this.usePostbackURL(data)

    if (url === null)
      return <SimpleAlert>
        Save the form to get the postback URL.
      </SimpleAlert>

    return <Row>
      <Col size='12'>
        <div className="float-right">
          <DropdownMenu size='sm'>
            <DropdownMenuTrigger asChild>
              <Button color='accent' variant='outline' className='flex items-center rounded-r-none' disabled={!url}>
                Optional Parameters
                <ChevronDown size={12} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                active={params.subid && params.subid.isAdded}
                onClick={() => params.subid.add()}
                disabled={!params.subid.optional}
              >
                External ID
              </DropdownMenuItem>
              <DropdownMenuItem
                active={params.transid && params.transid.isAdded}
                onClick={() => params.transid.add()}
                disabled={!params.transid.optional}
              >
                Transaction ID
              </DropdownMenuItem>
              <DropdownMenuItem
                active={params.format && params.format.isAdded}
                onClick={() => params.format.add()}
                disabled={!params.format.optional}
              >
                Image
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <FormSection>
          Postback URL
        </FormSection>
      </Col>
      <Col size='12'>
        <FormGroup>
          <Inputy type='hidden' value={url} name='url' />
          <URLView value={url}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button color='accent' className='flex items-center rounded-r-none' disabled={!url}>
                  GET
                  <ChevronDown size={12} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem disabled>
                  POST
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </URLView>
        </FormGroup>
      </Col>

      <Col size='12' className='flex flex-col gap-2'>
        <this.PostbackParam name='pekey' data={params} />
        <this.PostbackParam name='revenue' data={params} />
        <this.PostbackParam name='transid' data={params} />
        <this.PostbackParam name='subid' data={params} />
        <this.PostbackParam name='format' data={params} />
      </Col>
    </Row>
  }

  PostbackTransactionType = ({ data }) => {
    const [value, setValue] = useState('aggregated')

    useEffect(() => {
      handleChange(data)
    }, [data])

    const handleChange = (value) => {
      value && setValue(value)
      this.setState({ isIncremental: value === 'incremental' })
    }

    return <Row>
      <Col size='12'>
        <Label>
          Transaction Type
          <InfoTip>
            This controls how is returned from the API. Incremental Revenue means each <b><i>transid</i></b> is associeted to a revenue piece, then all revenues of differents <b><i>transid</i></b> going to be aggregated to the same <b><i>pe_key</i></b>. Aggregated means the revenue returned was already aggregated at the the third party level, then every new value for the same pekey going to be overriden.
          </InfoTip>
        </Label>
        <Select name='transaction_type' clearable={false} required
          value={value}
          options={TRANSACTION_TYPE_OPTIONS}
          onChange={({ value }) => handleChange(value)}
        />
      </Col>
      <Col size='12'>
        {this.state.isIncremental ? <SimpleAlert className='mb-2'>
          The Incremental transaction type needs the <b><i>transid</i></b> on the revenue url parameter. If no <b><i>transid</i></b> had passed then a internal <b><i>transid</i></b> going to be generated.
        </SimpleAlert> : null}
      </Col>
    </Row>
  }

  RevenuePostbackForm = ({ data: { url, transaction_type } }) => {
    return <Fragment>
      <this.PostbackTransactionType data={transaction_type} />
      <this.PostbackURL data={url} />
    </Fragment>
  }

  Partner = ({ name, data }) => {
    switch (name) {
      case 'twyne':
        return <Twyne name={name} data={data} />
      case 'ifficient':
        return <Ifficient name={name} data={data} />
      case 'optintelligence':
        return <OptIntelligence name={name} data={data} />
      case 'tiburon':
        return <Tiburon name={name} data={data} />
      case 'permissiondata':
        return <PermissionData name={name} data={data} />
      case 'covalent':
        return <CovalentPartner name={name} data={data} />
      case 'nitro':
        return <Nitro name={name} data={data} />
      case 'rexadz':
        return <RexadzPartner name={name} data={data} />
      case 'fluent':
        return <Fluent name={name} data={data} />
      case 'mediaalpha':
        return <MediaAlpha name={name} data={data} />
    }
  }

  RevenuePollerForm = ({ data }) => {
    const { vendor } = this.props
    const [partner, setPartner] = useState(data.partner || '')
    const partner_options = useMemo(() => PARTNERS_OPTIONS, [])

    useEffect(() => {
      if (!partner_options.some(item => item.value === partner)) {
        const [{ value = '' } = {}] = partner_options
        setPartner(value)
      }
    }, [vendor, partner])

    if (!partner)
      return null

    return <Fragment>
      <Row>
        <Col size='12'>
          <FormGroup>
            <Label>Partner</Label>
            <Select name='partner' clearable={false} required
              value={partner}
              options={partner_options}
              onChange={({ value }) => setPartner(value)}
            />
          </FormGroup>
        </Col>
        <FormFragment name={partner} >
          <Col size='12'>
            <this.Partner name={partner} data={data[partner]} />
          </Col>
        </FormFragment>
      </Row>
    </Fragment>
  }

  Type = ({ name, data }) => {
    switch (name) {
      case 'revenue_postback':
        return <this.RevenuePostbackForm data={data} />

      case 'revenue_poller':
        return <this.RevenuePollerForm data={data} />
    }
  }

  render() {
    const { type, configuration: { [type]: data = {} } } = this.props

    return <FormFragment name={['configuration', type]}>
      <this.Type name={type} data={data} />
    </FormFragment>
  }
}

RevenueForm.propTypes = propTypes
RevenueForm.defaultProps = {}
